import { store$, store } from '@redux/store';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs';
import { createAuthInstance, showModal, hideModal } from 'libraries/authLibrary';
import { onDocumentKeyDown$ } from 'layout/redux/observables';
import { isAuthLibraryLoaded, isAuthModalOpen } from '../constants/auth0Constants';
import { modalSlice, authenticationSlice } from '../redux/slices';
import getBaseConfig from './auth0BaseConfiguration';

const { actions: modalActions, reducer: modalReducers } = modalSlice;
const { reducer: authenticationReducers } = authenticationSlice;

const configuration = getBaseConfig();
const onCloseModal = () => store.dispatch(modalActions.onClose());
const initAuthentication = () => createAuthInstance(configuration, onCloseModal);
const openModal = () => showModal();
const userIdSelector = (state) => state?.userProfile?.userId;
const invalidValuesFilter = (value) => !!value;

const storeUserId = (userId) => {
  localStorage.setItem('UserId', userId);
};

const isEscapeKeyPressed = (event) => event.key === 'Escape';

const isTabKeyPressed = (event) => event.key === 'Tab';

/**
 * Retrieves the first and last focusable elements within the modal container.
 *
 * Focusable elements include: links, image maps, inputs, selects, textareas, and buttons
 * (excluding disabled elements).
 *
 * @function getFocusableElements
 * @returns {{ first: (Element|undefined), last: (Element|undefined) }}
 * An object containing the first and last focusable elements in the modal.
 */
const getFocusableElements = () => {
  const focusableTargets = `a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="button"]`;
  const auth0Modal = document.querySelector('.auth0-lock');
  const modalContainer = auth0Modal?.parentElement;

  if (!modalContainer) {
    return { first: null, last: null };
  }

  const focusableElements = modalContainer.querySelectorAll(focusableTargets);

  if (!focusableElements.length) {
    return { first: null, last: null };
  }

  return {
    first: focusableElements[0],
    last: focusableElements[focusableElements.length - 1],
  };
};

/**
 * Handles keyboard navigation within a modal using Tab and Shift+Tab keys.
 * Traps focus within the modal by cycling to the last focusable element when Tab is pressed on the first element,
 * and cycling to the first focusable element when Shift+Tab is pressed on the last element.
 *
 * @param {KeyboardEvent} event - The keyboard event triggered by pressing Tab or Shift+Tab.
 * @returns {void}
 */
const handleTabNavigation = (event) => {
  const { first, last } = getFocusableElements();
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
};

const onEscapeCloseModal$ = onDocumentKeyDown$.pipe(
  withLatestFrom(store$),
  filter(([, state]) => isAuthModalOpen(state)),
  map(([event]) => event),
  filter(isEscapeKeyPressed),
);

const onTabNavigation$ = onDocumentKeyDown$.pipe(
  withLatestFrom(store$),
  filter(([, state]) => isAuthModalOpen(state)),
  map(([event]) => event),
  filter(isTabKeyPressed),
);

onEscapeCloseModal$.subscribe(hideModal);

onTabNavigation$.subscribe(handleTabNavigation);

// We have to execute initAuthentication when the modal is not opened.
// So it means we need to track isAuthModalOpen.
// Where if it was already on true, we should not continue the flow.
// If it was on false, so we should check if the new isAuthModalOpen value is true or false to determine
// whether to continue the flow.
store$
  .pipe(filter(isAuthLibraryLoaded), map(isAuthModalOpen), distinctUntilChanged(), filter(Boolean))
  .subscribe(initAuthentication);

store$.pipe(filter(isAuthModalOpen), distinctUntilChanged()).subscribe(openModal);

store$.pipe(map(userIdSelector), distinctUntilChanged(), filter(invalidValuesFilter)).subscribe(storeUserId);

store.injectReducer('modal', modalReducers);
store.injectReducer('authentication', authenticationReducers);
